import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { ErrorBoundary } from '@/components/ErrorBoundary';
import { AppShell } from '@/components/layout';
import { MetaConnectionBanner } from '@/components/layout/MetaConnectionBanner';
import { ToastProvider } from '@/components/ui/Toast';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { MetaConnectionProvider } from '@/context/MetaConnectionContext';
import { ThemeProvider } from '@/context/ThemeContext';
import { queryClient } from '@/lib/queryClient';
// Old analytics pages removed in 28-09 (replaced by /dashboard)
import {
  Login,
  Register,
  ForgotPassword,
  VerifyEmail,
  TeamSetup,
} from '@/pages/auth';
import { UploadCreative, CreativeDetail, ComparisonView } from '@/pages/creative';
import { Dashboard } from '@/pages/dashboard';
import { AnalyticsDashboardPage } from '@/pages/dashboard/AnalyticsDashboard';
import { BrandPreviewPage } from '@/pages/dev/BrandPreviewPage';
import { ScopeToggleTestPage } from '@/pages/dev/ScopeToggleTestPage';
import { AcceptInvite } from '@/pages/invite';
import { DataDeletion, PrivacyPolicy, TermsOfService } from '@/pages/legal';
import { NotFound } from '@/pages/NotFound';
import { ProjectList, CreateProject, ProjectSettings, ProjectDashboard } from '@/pages/project';
import { ProfileSettings } from '@/pages/settings';
import { TeamSettings } from '@/pages/team';

import { AnalyticsDashboardV2Page } from '../prototype/pages/AnalyticsDashboardV2Page';
import { DashboardV3Page } from '../prototype/pages/DashboardV3Page';
import { TemplateEditorV2Page } from '../prototype/pages/TemplateEditorV2Page';
import { TemplateEditorV3Page } from '../prototype/pages/TemplateEditorV3Page';

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-canvas flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand mx-auto mb-4" />
        <p className="text-body">Loading...</p>
      </div>
    </div>
  );
}

// Protected route wrapper - requires authentication
function ProtectedRoute() {
  const { isAuthenticated, isLoading, teams } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Pass current location so Login can redirect back after authentication
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If authenticated but no teams, redirect to team setup
  if (teams.length === 0) {
    return <Navigate to="/team-setup" replace />;
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

// Public route wrapper (redirects to dashboard if already authenticated)
function PublicRoute() {
  const { isAuthenticated, isLoading, teams } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    // If authenticated but no teams, go to team setup
    if (teams.length === 0) {
      return <Navigate to="/team-setup" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
}

// Team setup route - requires authentication but no team
function TeamSetupRoute() {
  const { isAuthenticated, isLoading, teams } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // If user already has teams, redirect to dashboard
  if (teams.length > 0) {
    return <Navigate to="/" replace />;
  }

  return <TeamSetup />;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Public legal routes - intentionally outside auth wrappers */}
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/data-deletion" element={<DataDeletion />} />

      {/* Public routes */}
      <Route element={<PublicRoute />}>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/invite/:token" element={<AcceptInvite />} />
      </Route>

      {/* Team setup (authenticated but no team) */}
      <Route path="/team-setup" element={<TeamSetupRoute />} />

      {/* Protected routes */}
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<Dashboard />} />

        {/* Project routes */}
        <Route path="/projects" element={<ProjectList />} />
        <Route path="/projects/new" element={<CreateProject />} />
        <Route path="/projects/:projectId" element={<ProjectDashboard />} />
        <Route path="/projects/:projectId/settings" element={<ProjectSettings />} />
        <Route path="/projects/:projectId/upload" element={<UploadCreative />} />

        {/* Creative routes */}
        <Route path="/creatives/:creativeId" element={<CreativeDetail />} />
        <Route path="/compare" element={<ComparisonView />} />

        {/* Dashboard route - Feature: 009-dashboard-rework */}
        <Route path="/dashboard" element={<AnalyticsDashboardPage />} />

        {/* Old analytics routes removed — replaced by /dashboard (28-09) */}

        {/* Team routes */}
        <Route path="/teams/:teamId/settings" element={<TeamSettings />} />

        {/* Settings routes */}
        <Route path="/settings/profile" element={<ProfileSettings />} />
      </Route>

      {/* Dev-only test pages */}
      {import.meta.env.DEV && (
        <>
          <Route path="/dev/scope-toggle" element={<ScopeToggleTestPage />} />
          <Route path="/dev/brand-preview" element={<BrandPreviewPage />} />
          <Route path="/dev/template-editor-v2" element={<TemplateEditorV2Page />} />
          <Route path="/dev/analytics-dashboard-v2" element={<AnalyticsDashboardV2Page />} />
          <Route path="/dev/template-editor-v3" element={<TemplateEditorV3Page />} />
          <Route path="/dev/dashboard-v3" element={<DashboardV3Page />} />
        </>
      )}

      {/* 404 */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <QueryClientProvider client={queryClient}>
          <BrowserRouter>
            <ToastProvider>
              <AuthProvider>
                <MetaConnectionProvider>
                  <MetaConnectionBanner />
                  <AppRoutes />
                </MetaConnectionProvider>
              </AuthProvider>
            </ToastProvider>
          </BrowserRouter>
        </QueryClientProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
